/**
 * Portuguese language keywords for category prediction
 */
import type {
  AccommodationSubcategoryKeywords,
  ContextKeywords,
  DrinksConfig,
  DrinksSubcategoryKeywords,
  EntertainmentSubcategoryKeywords,
  FoodConfig,
  FoodSubcategoryKeywords,
  HealthcareSubcategoryKeywords,
  LanguageKeywords,
  MLPatternKeywords,
  ShoppingContextKeywords,
  ShoppingSubcategoryKeywords,
  StemmingConfig,
  TransportConfig,
  TransportKeywords,
  TransportSubcategoryKeywords,
  UtilitiesSubcategoryKeywords,
} from '../types';

// Transport-related keywords for disambiguation
export const transportKeywords: TransportKeywords = {
  general: [
    'trem',
    'ônibus',
    'busão',
    'voo',
    'metrô',
    'metro',
    'subway',
    'trilho',
    'bonde',
    'avião',
    'táxi',
    'taxi',
    'uber',
    'lyft',
    '99',
    'cabify',
    'trânsito',
    'transporte',
    'viagem',
    'aérea',
    'companhia aérea',
    'ferrovia',
    'trem',
    'van',
    'shuttle',
    'balsa',
    'barco',
    'carro',
    'carona',
    'corrida',
    'viagem',
    'jornada',
    'trajeto',
    'deslocamento',
    'aeroporto',
    'estação',
    'rodoviária',
    'terminal',
  ],
  tickets: ['bilhete', 'bilhetes', 'passagem', 'passagens', 'tarifa', 'passe'],
  tripContext: [
    'viagem',
    'viagem de carro',
    'viajar',
    'jornada',
    'férias',
    'dirigir',
    'direção',
    'passeio',
    'excursão',
  ],
};

// Transport subcategory keywords
export const transportSubcategories: TransportSubcategoryKeywords = {
  car: [
    'pagamento do carro',
    'financiamento de carro',
    'financiamento do veículo',
    'lavagem de carro',
    'lavagem do veículo',
    'seguro do carro',
    'seguro auto',
    'ipva',
    'detalhe do carro',
    'detalhamento automotivo',
  ],
  bus: [
    'ônibus',
    'busão',
    'coletivo',
    'van',
    'shuttle',
    'bilhete de ônibus',
    'passe de ônibus',
    'tarifa de ônibus',
    'passagem de ônibus',
    'rodoviária',
  ],
  // PT common words only — brand names live in `../shared/brands.ts`.
  train: [
    'trem',
    'trilho',
    'ferrovia',
    'bilhete de trem',
    'passagem de trem',
    'passe de trem',
    'bilhete único',
  ],
  tram: [
    'bonde',
    'vlt',
    'veículo leve sobre trilhos',
    'bilhete de bonde',
    'passagem de bonde',
  ],
  flight: [
    'voo',
    'aérea',
    'companhia aérea',
    'embarque',
    'aeroporto',
    'avião',
    'jato',
    'aviação',
    'passagem aérea',
    'bilhete aéreo',
    'passagem de avião',
    'cartão de embarque',
  ],
  bike: ['bike', 'bicicleta', 'patinete', 'scooter', 'ciclismo', 'pedalada'],
  boat: [
    'balsa',
    'barco',
    'navio',
    'cruzeiro',
    'marítimo',
    'iate',
    'embarcação',
    'veleiro',
    'catamarã',
    'lancha',
    'barca',
    'travessia',
  ],
  parking: [
    'estacionamento',
    'vaga',
    'zona azul',
    'parquímetro',
    'estapar',
    'taxa de estacionamento',
  ],
  taxi: [
    'táxi',
    'taxi',
    'praça',
    'taxista',
    'corrida de táxi',
    'tarifa de táxi',
    'ponto de táxi',
  ],
  rideshare: [
    'app de transporte',
    'corrida',
    'motorista de app',
    'transporte por aplicativo',
  ],
  metro: ['metrô', 'metro', 'subterrâneo', 'linha', 'estação de metrô'],
  fuel: [
    'gasolina',
    'álcool',
    'etanol',
    'diesel',
    'combustível',
    'posto',
    'posto de gasolina',
    'abastecimento',
    'abastecer',
    'tanque cheio',
    'gnv',
  ],
  toll: ['pedágio', 'praça de pedágio', 'cancela', 'tarifa de pedágio'],
  rental: [
    'aluguel de carro',
    'locadora',
    'locação de veículo',
    'carro alugado',
  ],
  maintenance: [
    'troca de óleo',
    'pneu',
    'mecânico',
    'oficina mecânica',
    'funilaria',
    'borracharia',
    'alinhamento',
    'balanceamento',
    'freios',
    'suspensão',
  ],
  airport: [
    'aeroporto',
    'terminal aeroporto',
    'estacionamento aeroporto',
    'transfer aeroporto',
    'embarque',
    'desembarque',
  ],
};

// Only the generic word is shared by food and healthcare: on its own it
// cannot say sports nutrition or health, so the cloud tier breaks that
// tie. Specific products go to one parent so the free tier can answer.
const supplementsKeywords: string[] = ['suplementos', 'suplemento'];
const sportsNutritionKeywords: string[] = [
  'proteína',
  'proteina',
  'whey',
  'creatina',
  'maltodextrina',
  'albumina',
  'termogênico',
  'termogenico',
  'glutamina',
  'bcaa',
  'pré-treino',
  'pre treino',
];
const healthSupplementsKeywords: string[] = [
  'colágeno',
  'colageno',
  'vitaminas',
  'vitamina',
  'ômega 3',
  'omega 3',
  'omega-3',
  'probióticos',
  'probioticos',
  'magnésio',
  'magnesio',
  'multivitamínico',
  'multivitaminico',
  'polivitamínico',
];

// Food subcategory keywords (partial list for demonstration)
export const foodSubcategories: Partial<FoodSubcategoryKeywords> = {
  // PT common words only — brands + universal food terms live in
  // `../shared/brands.ts`.
  pizza: [
    'pizzaria',
    'rodízio de pizza',
    'entrega de pizza',
    'delivery de pizza',
  ],
  sushi: ['tempurá', 'japa'],
  hamburger: ['hambúrguer', 'x-burger', 'sanduíche', 'lanche'],
  sandwich: ['pão com', 'beirute', 'misto', 'bauru', 'sanduíche natural'],
  soup: [
    'sopa',
    'caldo',
    'canja',
    'consomê',
    'feijão',
    'feijoada',
    'caldinho',
    'lámen',
    'sopa de legumes',
    'caldo verde',
    'sopa de galinha',
  ],
  steak: [
    'picanha',
    'contrafilé',
    'alcatra',
    'filé mignon',
    'churrascaria',
    'maminha',
  ],
  salad: [
    'salada',
    'salada caesar',
    'salada grega',
    'salada mista',
    'salada da casa',
  ],
  pastry: [
    'confeitaria',
    'sonho',
    'pão de queijo',
    'pão doce',
    'broa',
    'rosquinha',
  ],
  dessert: [
    'sobremesa',
    'bolo',
    'torta',
    'biscoito',
    'pudim',
    'brigadeiro',
    'beijinho',
    'quindim',
  ],
  candy: ['doce', 'doces', 'balas', 'gomas', 'pirulito', 'paçoca'],
  icecream: ['sorvete', 'sorvetes', 'picolé', 'açaí'],
  snacks: [
    'lanchinho',
    'salgadinho',
    'biscoito',
    'pipoca',
    'batata frita',
    'amendoim',
    'castanha',
    'petisco',
    'tira-gosto',
  ],
  fruit: [
    'fruta',
    'frutas',
    'maçã',
    'laranja',
    'manga',
    'abacaxi',
    'morango',
    'uva',
    'melancia',
  ],
  vegetables: [
    'vegetal',
    'vegetais',
    'verdura',
    'legume',
    'legumes',
    'folhas',
    'hortaliça',
  ],
  meat: [
    'açougue',
    'carne',
    'boi',
    'porco',
    'cordeiro',
    'frango',
    'peru',
    'presunto',
    'linguiça',
  ],
  seafood: [
    'frutos do mar',
    'peixe',
    'salmão',
    'atum',
    'camarão',
    'lagosta',
    'caranguejo',
    'ostra',
    'polvo',
    'lula',
  ],
  dairy: [
    'laticínios',
    'leite',
    'queijo',
    'iogurte',
    'manteiga',
    'creme',
    'requeijão',
  ],
  breakfast: [
    'café da manhã',
    'desjejum',
    'tapioca',
    'pão com manteiga',
    'misto quente',
    'cereais',
    'mingau',
    'cuscuz',
  ],
  restaurant: [
    'restaurante',
    'bistrô',
    'churrascaria',
    'rodízio',
    'italiano',
    'chinês',
    'mexicano',
    'indiano',
    'tailandês',
    'vietnamita',
    'coreano',
    'japonês',
    'brasileiro',
    'self service',
    'por quilo',
    'à la carte',
    'lanchonete',
    'boteco',
    'bar e restaurante',
    'lanche rápido',
    'almoço',
    'prato feito',
    'pf',
    'marmita',
    'quentinha',
    'executivo',
    'jantar',
    'janta',
    'refeição noturna',
    'ceia',
  ],
  bakery: [
    'padaria',
    'pão',
    'pão francês',
    'salgado',
    'pastel',
    'coxinha',
    'empada',
  ],
  // Venues only: in Portuguese a bare 'café' is the drink (drinks/coffee).
  cafe: ['cafeteria', 'cafetaria', 'coffee shop'],
  delivery: [
    'entrega',
    'para viagem',
    'retirada',
    'entrega de comida',
    'tele-entrega',
  ],
  groceries: ['mercado', 'supermercado', 'compras'],
  supplements: [...sportsNutritionKeywords, ...supplementsKeywords],
};

export const drinksSubcategories: DrinksSubcategoryKeywords = {
  // PT common words only — brands live in `../shared/brands.ts`.
  // Bare "water" words stay out: they collide with utilities/water.
  drinks: ['bebida', 'bebidas'],
  water: [
    'água mineral',
    'água com gás',
    'água sem gás',
    'garrafa de água',
    'galão de água',
  ],
  juice: [
    'suco',
    'sucos',
    'suquinho',
    'suco de laranja',
    'suco natural',
    'caldo de cana',
    'água de coco',
  ],
  soda: [
    'refrigerante',
    'refrigerantes',
    'refri',
    'guaraná',
    'guarana',
    'coca',
    'soda',
    'limonada',
  ],
  // 'café da manhã' (food/breakfast) still wins as the longer phrase.
  coffee: [
    'café',
    'cafe',
    'cafés',
    'cafezinho',
    'café com leite',
    'café expresso',
    'expresso',
    'café gelado',
    'pingado',
    'bica',
  ],
  tea: ['chá', 'chá gelado', 'chá verde', 'chimarrão', 'tereré'],
  energy: ['energético', 'energéticos', 'bebida energética', 'isotônico'],
  beer: [
    'cerveja',
    'cervejas',
    'cervejinha',
    'chopp',
    'chope',
    'breja',
    'long neck',
  ],
  wine: [
    'vinho',
    'vinhos',
    'vinho tinto',
    'vinho branco',
    'taça de vinho',
    'garrafa de vinho',
  ],
  cocktails: [
    'coquetel',
    'coquetéis',
    'drink',
    'drinks',
    'batida',
    'caipiroska',
  ],
  spirits: [
    'cachaça',
    'cachaca',
    'pinga',
    'aguardente',
    'conhaque',
    'uísque',
    'vodca',
    'gim',
  ],
  alcohol: [
    'bebida alcoólica',
    'bebidas alcoólicas',
    'álcool',
    'adega',
    'depósito de bebidas',
  ],
  bar: [
    'bar',
    'barzinho',
    'boteco',
    'botequim',
    'cervejaria',
    'choperia',
    'pub',
    'happy hour',
  ],
};

// Context keywords
export const contextKeywords: ContextKeywords = {
  flight: ['bagagem', 'mala'],
  fuel: [
    'viagem',
    'viagem de carro',
    'viajar',
    'jornada',
    'férias',
    'dirigir',
    'direção',
    'carro',
    'veículo',
    'quilômetros',
    'km',
    'estrada',
  ],
  transportTickets: [
    'bilhete',
    'bilhetes',
    'passagem',
    'passagens',
    'passe',
    'tarifa',
    'trânsito',
    'transporte',
    'deslocamento',
    'jornada',
    'viajar',
    'viagem',
  ],
};

// Entertainment subcategory keywords
const entertainmentSubcategories: EntertainmentSubcategoryKeywords = {
  // PT common words only — brands + universal terms live in
  // `../shared/brands.ts`.
  music: [
    'show',
    'concerto',
    'ingressos',
    'música',
    'álbum',
    'canção',
    'artista',
    'banda',
    'vinil',
    'cd',
  ],
  streaming: ['assinatura'],
  gaming: ['jogo', 'jogos', 'lan house'],
  books: [
    'livro',
    'livros',
    'revista',
    'jornal',
    'biblioteca',
    'livraria',
    'leitura',
    'romance',
    'literatura',
    'gibi',
    'hq',
  ],
  fitness: [
    'academia',
    'ioga',
    'treino',
    'musculação',
    'personal',
    'personal trainer',
  ],
  movies: ['filme', 'cinema', 'sessão', 'ingresso cinema', 'matinê'],
  events: [
    'show',
    'concerto',
    'música ao vivo',
    'festival',
    'performance',
    'apresentação',
    'museu',
    'galeria',
    'exposição',
    'centro cultural',
    'mostra',
    'evento',
    'conferência',
    'convenção',
    'encontro',
    'reunião',
    'festa junina',
    'carnaval',
    'celebração',
    'teatro',
    'peça',
    'musical',
    'ópera',
    'espetáculo',
    'balada',
    'boate',
    'clube',
    'lounge',
    'festa',
    'ingresso',
    'ingressos',
    'bilhete',
  ],
  outdoor: [
    'parque',
    'parque de diversões',
    'zoológico',
    'aquário',
    'trilha',
    'acampamento',
    'pesca',
    'pescaria',
    'caça',
    'aventura',
    'natureza',
    'cachoeira',
    'montanha',
    'escalada',
    'trekking',
    'mochilão',
    'rapel',
    'alpinismo',
    'ecoturismo',
  ],
  sports: [
    'futebol',
    'vôlei',
    'basquete',
    'tênis',
    'natação',
    'corrida',
    'golfe',
    'estádio',
    'arena',
    'jogo',
    'partida',
    'campeonato',
    'ingresso jogo',
  ],
  photography: [
    'câmera',
    'foto',
    'fotografia',
    'lente',
    'fotógrafo',
    'sessão de fotos',
    'ensaio fotográfico',
  ],
  podcasts: ['rádio', 'episódio', 'flow'],
  water: [
    'mergulho',
    'diving',
    'surfe',
    'caiaque',
    'paraquedas',
    'praia',
    'natação',
    'esportes aquáticos',
    'vela',
    'lancha',
    'esqui aquático',
    'aquático',
    'marítimo',
  ],
};

// Shopping subcategory keywords
const shoppingSubcategories: ShoppingSubcategoryKeywords = {
  // PT common words only — brands live in `../shared/brands.ts`.
  clothes: [
    'roupa',
    'roupas',
    'camisa',
    'camiseta',
    'calça',
    'vestido',
    'jaqueta',
    'blusa',
    'vestuário',
    'moda',
    'fashion',
    'outfit',
    'guarda-roupa',
    'peça',
  ],
  electronics: [
    'eletrônicos',
    'notebook',
    'laptop',
    'celular',
    'telefone',
    'computador',
    'tablet',
    'fone',
    'headphone',
    'gadget',
    'eletrodomésticos',
    'geladeira',
    'fogão',
    'máquina de lavar',
    'lavadora',
    'secadora',
    'lava-louças',
    'micro-ondas',
    'forno',
  ],
  groceries: ['mercado', 'supermercado', 'compras', 'compra do mês'],
  online: ['online', 'compra online', 'ecommerce', 'shopee'],
  furniture: [
    'móveis',
    'mobiliário',
    'cadeira',
    'mesa',
    'sofá',
    'cama',
    'escrivaninha',
    'armário',
    'decoração',
    'utilidades domésticas',
    'reforma',
    'interior',
    'enxoval',
  ],
  gifts: [
    'presente',
    'presentes',
    'aniversário',
    'natal',
    'dia das mães',
    'dia dos pais',
    'flores',
    'cartão',
    'lembrança',
  ],
  services: [
    'encanador',
    'eletricista',
    'pedreiro',
    'empreiteiro',
    'reparo',
    'conserto',
    'manutenção',
    'serviço',
    'técnico',
    'pintor',
    'marceneiro',
    'chaveiro',
    'limpeza',
    'faxina',
    'diarista',
    'instalação',
    'ar condicionado',
  ],
  shoes: [
    'sapato',
    'sapatos',
    'calçados',
    'tênis',
    'bota',
    'sandália',
    'chinelo',
  ],
  books: ['livro', 'livros', 'livraria', 'revista', 'gibi'],
  toys: ['brinquedo', 'brinquedos', 'pelúcia'],
  mall: ['shopping', 'shopping center', 'centro comercial', 'galeria'],
  market: ['mercado', 'feira', 'feira livre', 'mercadão', 'ceasa', 'sacolão'],
  department: ['loja de departamento'],
  jewelry: [
    'joias',
    'joalheria',
    'anel',
    'colar',
    'pulseira',
    'relógio',
    'brinco',
    'acessórios',
    'bolsa',
    'bolsas',
    'carteira',
    'cinto',
    'cachecol',
    'chapéu',
    'boné',
  ],
  cosmetics: [
    'cosméticos',
    'maquiagem',
    'beleza',
    'perfume',
    'skincare',
    'cuidados com o cabelo',
    'salão',
    'estética',
  ],
  sports: [
    'esportes',
    'esportivo',
    'artigos esportivos',
    'equipamento esportivo',
    'equipamento de academia',
    'atlético',
    'fitness',
  ],
  hardware: [
    'ferragem',
    'loja de ferramentas',
    'c&c',
    'ferramentas',
    'material de construção',
  ],
  garden: [
    'jardim',
    'jardinagem',
    'plantas',
    'flores',
    'garden center',
    'móveis de jardim',
    'varanda',
    'quintal',
    'horta',
  ],
};

// Accommodation subcategory keywords
const accommodationSubcategories: AccommodationSubcategoryKeywords = {
  // PT common words only — brands live in `../shared/brands.ts`.
  hotel: [
    'hotel',
    'motel',
    'pousada',
    'resort',
    'hospedagem',
    'suíte',
    'diária',
    'resort de praia',
    'resort de montanha',
    'spa resort',
    'all inclusive',
    'tudo incluído',
    'pernoite',
    'hospedagem de estrada',
    'motel econômico',
    'quarto',
    'acomodação',
    'cômodo',
  ],
  airbnb: [
    'aluguel de temporada',
    'casa de temporada',
    'imóvel para temporada',
    'aluguel por temporada',
    'hospedagem alternativa',
  ],
  apartment: [
    'apartamento',
    'apto',
    'flat',
    'studio',
    'kit',
    'kitnet',
    'quitinete',
    'loft',
    'cobertura',
    'duplex',
  ],
  house: [
    'casa',
    'residência',
    'moradia',
    'chácara',
    'sítio',
    'mansão',
    'sobrado',
    'vila',
  ],
  rent: [
    'aluguel',
    'locação',
    'locatário',
    'proprietário',
    'inquilino',
    'aluguel mensal',
    'pagamento de aluguel',
    'contrato de locação',
  ],
  hostel: [
    'hostel',
    'albergue',
    'dormitório',
    'cama em dormitório',
    'acomodação econômica',
    'albergue da juventude',
    'mochileiro',
  ],
  camping: [
    'camping',
    'acampamento',
    'barraca',
    'trailer',
    'motorhome',
    'caravana',
    'área de camping',
    'glamping',
  ],
};

// Healthcare subcategory keywords
const healthcareSubcategories: HealthcareSubcategoryKeywords = {
  // PT common words only — brands live in `../shared/brands.ts`.
  doctor: [
    'médico',
    'doutor',
    'clínica',
    'consulta',
    'consultório',
    'clínico geral',
    'checkup',
    'exame',
    'consulta médica',
    'posto de saúde',
  ],
  pharmacy: [
    'farmácia',
    'drogaria',
    'medicamento',
    'remédio',
    'prescrição',
    'receita',
  ],
  hospital: [
    'hospital',
    'emergência',
    'pronto socorro',
    'ps',
    'upa',
    'cirurgia',
    'operação',
    'internação',
    'uti',
    'ambulância',
    'resgate',
    'laboratório',
    'exame de sangue',
    'exame laboratorial',
    'diagnóstico',
  ],
  dental: [
    'dental',
    'dentista',
    'dente',
    'dentes',
    'ortodontista',
    'oral',
    'limpeza',
    'aparelho',
    'consulta odontológica',
    'tratamento dentário',
    'canal',
    'obturação',
  ],
  vision: [
    'visão',
    'olhos',
    'oftalmologista',
    'óculos',
    'ótica',
    'lentes de contato',
    'lentes',
    'exame de vista',
    'oculista',
    'grau',
  ],
  specialist: [
    'especialista',
    'consulta com especialista',
    'dermatologista',
    'cardiologista',
    'pediatra',
    'psiquiatra',
    'ginecologista',
    'ortopedista',
    'neurologista',
    'terapia',
    'saúde mental',
    'psicólogo',
    'sessão de terapia',
    'consulta psicológica',
    'fisioterapia',
    'reabilitação',
    'fono',
    'fonoaudiologia',
    'terapia ocupacional',
    'reeducação postural',
  ],
  insurance: [
    'seguro',
    'plano de saúde',
    'convênio',
    'carterinha',
    'reembolso',
  ],
  fitness: [
    'academia',
    'mensalidade academia',
    'fitness',
    'centro fitness',
    'health club',
    'treino',
    'exercício',
    'personal trainer',
    'personal',
    'ioga',
    'musculação',
    'bem estar',
    'wellness',
    'spa',
    'medicina alternativa',
    'acupuntura',
    'massagem',
    'holístico',
  ],
  supplements: [...healthSupplementsKeywords, ...supplementsKeywords],
};

// Utilities subcategory keywords
const utilitiesSubcategories: UtilitiesSubcategoryKeywords = {
  // PT common words only — brand providers live in `../shared/brands.ts`.
  electricity: [
    'eletricidade',
    'luz',
    'energia',
    'energia elétrica',
    'conta de luz',
    'kilowatt',
  ],
  water: ['água', 'conta de água', 'saneamento', 'esgoto'],
  gas: [
    'gás',
    'gás natural',
    'conta de gás',
    'gás encanado',
    'botijão',
    'gás de cozinha',
    'glp',
    'aquecimento',
    'calefação',
    'aquecedor',
  ],
  internet: [
    'internet',
    'wifi',
    'banda larga',
    'fibra',
    'fibra óptica',
    'cabo',
    'provedor',
  ],
  phone: [
    'telefone',
    'celular',
    'móvel',
    'conta de celular',
    'plano de dados',
    'plano de celular',
  ],
  trash: [
    'lixo',
    'coleta de lixo',
    'resíduos',
    'reciclagem',
    'saneamento',
    'taxa de lixo',
    'coleta',
  ],
  maintenance: [
    'manutenção',
    'condomínio',
    'taxa de condomínio',
    'manutenção predial',
    'conservação',
    'reparos',
    'administração',
    'síndico',
    'manutenção residencial',
  ],
  streaming: [
    'assinatura',
    'serviço de streaming',
    'tv a cabo',
    'televisão',
    'claro tv',
    'vivo tv',
  ],
};

// Shopping context keywords for better subcategory detection
const shoppingContext: ShoppingContextKeywords = {
  clothes: ['moda', 'vestuário', 'varejo', 'roupa'],
  electronics: ['tecnologia', 'dispositivo', 'gadget', 'eletrônico'],
  furniture: ['showroom', 'decoração', 'interior', 'móveis'],
  online: ['pedido', 'frete', 'rastreamento'],
  groceries: ['semanal', 'mensal', 'lista de compras', 'feira'],
  services: ['consertar', 'reparo', 'quebrado', 'instalar', 'manutenção'],
};

// ML model pattern keywords for fast matching
const mlPatterns: MLPatternKeywords = {
  expense: {
    transport: [
      'uber',
      '99',
      'táxi',
      'ônibus',
      'trem',
      'voo',
      'gasolina',
      'estacionamento',
      'metrô',
      'combustível',
      'férias',
      'viagem',
      'passagem',
    ],
    food: [
      'restaurante',
      'almoço',
      'jantar',
      'mercado',
      'café',
      'pizza',
      'comida',
      'café da manhã',
      'ifood',
      'lanche',
    ],
    entertainment: [
      'netflix',
      'spotify',
      'cinema',
      'show',
      'jogo',
      'academia',
      'globoplay',
    ],
    shopping: [
      'amazon',
      'mercado livre',
      'shopee',
      'roupa',
      'compras',
      'shopping',
    ],
    healthcare: [
      'médico',
      'hospital',
      'farmácia',
      'dentista',
      'consulta',
      'plano de saúde',
    ],
    utilities: ['luz', 'água', 'internet', 'celular', 'conta', 'condomínio'],
    accommodation: ['hotel', 'airbnb', 'aluguel', 'apartamento', 'pousada'],
    education: [
      'curso',
      'aula',
      'escola',
      'universidade',
      'faculdade',
      'livro',
      'apostila',
      'udemy',
      'coursera',
      'treinamento',
      'certificação',
      'workshop',
      'seminário',
      'mensalidade',
      'educação',
      'estudo',
      'material escolar',
    ],
  },
  group: {
    friends: [
      'amigos',
      'amizade',
      'galera',
      'turma',
      'pessoal',
      'grupo',
      'social',
      'encontro',
      'reunião',
      'rolê',
      'saída',
      'balada',
      'jantar',
      'jogo',
      'noite',
      'fim de semana',
      'happy hour',
      'churrasco',
    ],
    family: [
      'família',
      'pais',
      'irmãos',
      'parentes',
      'reunião familiar',
      'avós',
      'filhos',
      'primos',
      'casa',
      'lar',
      'parentesco',
      'herança',
      'férias',
      'feriado',
      'refeição',
      'compras',
      'mercado',
      'compartilhado',
    ],
    couple: [
      'casal',
      'namorado',
      'namorada',
      'parceiro',
      'parceira',
      'cônjuge',
      'marido',
      'esposa',
      'relacionamento',
      'namoro',
      'amor',
      'juntos',
      'encontro',
      'noite',
      'conta',
      'contas',
      'apartamento',
      'despesa',
      'despesas',
    ],
    events: [
      'festa',
      'festas',
      'casamento',
      'aniversário',
      'celebração',
      'bodas',
      'formatura',
      'show',
      'festival',
      'evento',
      'cerimônia',
      'recepção',
      'encontro',
      'especial',
      'chá de bebê',
      'chá de panela',
      'chá de lingerie',
      'despedida de solteiro',
    ],
    roommates: [
      'colegas de quarto',
      'colega',
      'apartamento',
      'casa',
      'aluguel',
      'contrato',
      'compartilhado',
      'moradia',
      'república',
      'divisão',
      'locação',
      'residência',
      'contas',
      'utilidades',
      'casa compartilhada',
      'suprimentos',
      'luz',
      'água',
      'internet',
      'limpeza',
      'faxina',
    ],
    trip: [
      'viagem',
      'viajar',
      'férias',
      'feriado',
      'lua de mel',
      'cruzeiro',
      'excursão',
      'passeio',
      'aventura',
      'jornada',
      'destino',
      'explorar',
      'mochilão',
      'turismo',
      'hotel',
      'hotéis',
      'voo',
      'voos',
      'refeição',
      'refeições',
      'restaurante',
      'gastronomia',
    ],
    work: [
      'trabalho',
      'escritório',
      'projeto',
      'equipe',
      'empresa',
      'negócio',
      'startup',
      'conferência',
      'reunião',
      'corporativo',
      'colegas',
      'profissional',
      'departamento',
      'local de trabalho',
      'almoço',
      'almoços',
      'suprimentos',
      'material de escritório',
      'eventos',
      'evento',
      'happy hour',
    ],
  },
};

// Food configuration
const foodConfig: FoodConfig = {
  obviousSubcategories: ['fruit', 'vegetables'],
  subcategoryPriority: ['pizza', 'hamburger', 'sandwich', 'soup', 'cafe'],
};

const drinksConfig: DrinksConfig = {
  subcategoryPriority: [
    'cocktails',
    'beer',
    'wine',
    'spirits',
    'energy',
    'coffee',
    'tea',
    'juice',
    'soda',
    'water',
    'bar',
    'alcohol',
    'drinks',
  ],
};

// Transport configuration
const transportConfig: TransportConfig = {
  subcategoryPriority: [
    'rental',
    'flight',
    'airport',
    'taxi',
    'rideshare',
    'car',
    'bike',
    'boat',
    'train',
    'tram',
  ],
};

// Portuguese stemming configuration
const stemming: StemmingConfig = {
  suffixes: [
    'ando',
    'endo',
    'indo',
    'ar',
    'er',
    'ir',
    'ção',
    'mento',
    's',
    'es',
    'mente',
    'dor',
    'dora',
  ],
};

// Complete Portuguese language keyword configuration
export const ptKeywords: LanguageKeywords = {
  transport: transportKeywords,
  transportSubcategories,
  foodSubcategories: foodSubcategories as FoodSubcategoryKeywords,
  drinksSubcategories,
  entertainmentSubcategories,
  shoppingSubcategories,
  accommodationSubcategories,
  healthcareSubcategories,
  utilitiesSubcategories,
  context: contextKeywords,
  shoppingContext,
  foodConfig,
  drinksConfig,
  transportConfig,
  mlPatterns,
  stemming,
};
